import { useParams, useSearchParams } from 'react-router-dom';
import { GetCampaignParams, useGetCampaignV3Query } from '../api';
import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

export type UseCampaignHook = ReturnType<typeof useCampaign>;

export const useCampaign = (overrideParams?: GetCampaignParams) => {
  const campaignParams = {
    ...useCampaignParameters(overrideParams?.id),
    ...overrideParams,
  };
  const { data, isFetching, error } = useGetCampaignV3Query(
    campaignParams.id ? campaignParams : skipToken
  );
  return useMemo(
    () => ({
      campaign: data,
      isFetching,
      error,
    }),
    [data, isFetching, error]
  );
};

export const useCurrentCampaign = () => {
  const { id: campaignId } = useParams<{ id: string }>();
  if (!campaignId) {
    throw new Error('No campaign ID found for current page');
  }

  return useCampaign({ id: Number(campaignId) });
};

export const useCampaignParameters = (
  campaignId?: number
): GetCampaignParams => {
  const { id: campaignIdFromParams } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const customReportId = searchParams.get('custom_report_id') ?? undefined;
  const includeUnapproved = searchParams.get('include_unapproved') === 'true';

  return useMemo(
    () => ({
      id:
        (campaignId ?? campaignIdFromParams) ? Number(campaignIdFromParams) : 0,
      custom_report_id: customReportId,
      include_unapproved: includeUnapproved,
    }),
    [campaignId, customReportId, includeUnapproved, campaignIdFromParams]
  );
};
